import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaUserEdit, FaGem, FaCrown, FaQrcode, FaHistory, FaCog, FaEye, FaEyeSlash } from "react-icons/fa";
import { MdSubscriptions } from "react-icons/md";
import Addresses from "./Addresses";
import EditPage from "./EditPage";
import ProfilePictureUpload from "../Try/ProfilePicComponent";
import zIndex from "@mui/material/styles/zIndex";
import "../../styles/scrollbar.css";
import shareIcon from "../Assets/share.png";
import { useTranslation } from "react-i18next"; // Add this import
import profileContainer from "../Assets/profilecontainer.png"
import profilebg from "../Assets/bgprofile.PNG"
import { fetchLanguages } from "../../i18n";

const REQUEST_TTL_MS = 5 * 60 * 1000;
const accountRequestCache = new Map();

const getCachedRequest = async (cacheKey, requestFactory) => {
  const cachedEntry = accountRequestCache.get(cacheKey);
  const now = Date.now();

  if (cachedEntry) {
    if (cachedEntry.data && now - cachedEntry.timestamp < REQUEST_TTL_MS) {
      return cachedEntry.data;
    }

    if (cachedEntry.promise) {
      return cachedEntry.promise;
    }
  }

  const requestPromise = requestFactory()
    .then((data) => {
      accountRequestCache.set(cacheKey, { data, timestamp: Date.now() });
      return data;
    })
    .catch((error) => {
      accountRequestCache.delete(cacheKey);
      throw error;
    });

  accountRequestCache.set(cacheKey, { promise: requestPromise, timestamp: now });
  return requestPromise;
};
const styles = {
  panel: {
    display: "flex",
    flexDirection: "column",
    padding: "20px",
    borderRadius: "12px",
    backgroundColor: "black",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
    width: "100%",
    maxWidth: "1200px",
    minHeight: "120vh",
    margin: "20px auto",
    marginBottom: "300px", // Extra space for footer on larger screens
    position: "relative",
    zIndex: 1,
    // Responsive adjustments
    "@media (max-width: 1024px)": {
      maxWidth: "95%",
      padding: "15px",
      marginBottom: "250px",
    },
    "@media (max-width: 768px)": {
      padding: "10px",
      margin: "10px auto",
      marginBottom: "200px", // Less space needed on mobile
      minHeight: "100vh",
    },
    "@media (max-width: 480px)": {
      padding: "8px",
      marginBottom: "150px",
      minHeight: "90vh",
    },
  },
  header: {
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "20px",
    color: "#DFBD69",
  },
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr",
    gap: "20px",
    marginBottom: "20px",
    // Responsive: stack on mobile
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr", // Single column on mobile
      gap: "15px",
    },
  },
  card: {
    padding: "25px",
    borderRadius: "8px",
    borderTop: "2px solid transparent",
    background: "linear-gradient(to top, #DFBD69, #b7954d, #926F34)",
    color: "#000",
    minHeight: "500px",
    // Responsive adjustments
    "@media (max-width: 768px)": {
      padding: "15px",
      minHeight: "300px", // Smaller height on mobile
    },
    "@media (max-width: 480px)": {
      padding: "10px",
      minHeight: "250px",
    },
  },
  header: {
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "20px",
    color: "#DFBD69",
  },
  changePassButton: {
    padding: "0",
    fontSize: "16px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    color: "#000000",
    textAlign: "left",
    alignItems: "center",
    display: "inline", // Change from "block" to "inline"
    marginBottom: "0", // Remove bottom margin
    fontFamily: "inherit",
    lineHeight: "1.5",
    fontWeight: "normal",
    marginLeft: "5px", // Add a small margin to separate from text
    textDecoration: "underline", // Optional: make it look more like a link
  },
  button: {
    padding: "5px 20px",
    fontSize: "16px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    // marginBottom: "10px",
    backgroundColor: "#DFBD69",
    color: "#000000",
    textAlign: "center",
    alignItems: "center",
  },
};

const AccountInfoPanel = ({ onToggle, style }) => {
  // Responsive hook
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  const isMobile = windowWidth <= 768;
  const isSmallMobile = windowWidth <= 480;

  // Dynamic responsive styles
  const responsiveStyles = {
    panel: {
      display: "flex",
      flexDirection: "column",
      padding: isSmallMobile ? "8px" : isMobile ? "12px" : "12px",
      borderRadius: "12px",
      //backgroundColor: "white",
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
      width: "100%",
      maxWidth: isMobile ? "95%" : "1200px",
      minHeight: isSmallMobile ? "auto" : isMobile ? "auto" : "auto",
      overflowY: "visible",
      margin: isMobile ? "10px auto" : "10px auto",
      marginBottom: isSmallMobile ? "200px" : isMobile ? "250px" : "300px", // Increased spacing
      paddingBottom: isSmallMobile ? "80px" : isMobile ? "100px" : "150px", // Increased padding
      position: "relative",
      zIndex: 1,
    },
    header: {
      fontSize: isMobile ? "20px" : "24px",
      fontWeight: "bold",
      marginBottom: "20px",
      color: "#DFBD69",
    },
    gridContainer: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
      gap: isMobile ? "15px" : "10px",
      marginBottom: "20px",
    },
    card: {
      padding: isSmallMobile ? "12px" : isMobile ? "15px" : "25px",
      borderRadius: "8px",
      borderTop: "2px solid transparent",
      background: "linear-gradient(to top, #DFBD69, #b7954d, #926F34)",
      color: "#000",
      height: isSmallMobile ? "auto" : isMobile ? "auto" : "auto",
      //minHeight: isSmallMobile ? "auto" : isMobile ? "200px" : "500px",
      marginBottom: isMobile ? "10px" : "20px",
      // maxHeight: "90vh", // Add this line
      // overflowY: "auto", // Add this line
    },
    info: {
      fontSize: isMobile ? "14px" : "16px",
      marginBottom: "8px",
      lineHeight: "1.4",
      color: "black",
    },
    changePassButton: {
      padding: "0",
      fontSize: isMobile ? "14px" : "16px",
      borderRadius: "4px",
      border: "none",
      cursor: "pointer",
      backgroundColor: "transparent",
      color: "#000000",
      textAlign: "left",
      alignItems: "center",
      display: "inline",
      marginBottom: "0",
      fontFamily: "inherit",
      lineHeight: "1.5",
      fontWeight: "normal",
      marginLeft: "5px",
      textDecoration: "underline",
    },
    button: {
      padding: isMobile ? "8px 16px" : "5px 20px",
      fontSize: isMobile ? "14px" : "16px",
      borderRadius: "4px",
      border: "none",
      cursor: "pointer",
      backgroundColor: "#DFBD69",
      color: "#000000",
      textAlign: "center",
      alignItems: "center",
    },
  };
  const { t, i18n } = useTranslation(); // Add this hook
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [chineseDob, setChineseDob] = useState("");
  const [gender, setGender] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [age, setAge] = useState(null);
  const [interest, setInterest] = useState("null");
  const [country, setCountry] = useState("");
  const [companyID, setCompanyID] = useState("SGA27");
  const [vvp, setVVP] = useState("");
  const [vp, setVP] = useState("");
  const [member, setMember] = useState("Nil");
  const [isEditing, setIsEditing] = useState(false);
  const [coins, setCoins] = useState(0);
  const [comcoins, setComCoins] = useState(0);
  const [aletaMemberNo, setAletaMemberNo] = useState("");
  const [aletaAccountNo, setAletaAccountNo] = useState("");
  const [aletaAccountSerialNo, setAletaAccountSerialNo] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('language') || 'en');
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const navigate = useNavigate();

  const userID = localStorage.getItem("userID");
  const apiUrl = `https://xianhefairy.com/LotusFairy_API/api/booking/getTotal?ID=${userID}`;

  const fetchData = async () => {
    try {
      const data = await getCachedRequest(`coins:${userID}`, async () => {
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }

        return response.json();
      });

      setCoins(data?.[0]?.COINS ?? 0);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchComs = async () => {
    try {
      const data = await getCachedRequest(`commission:${userID}`, async () => {
        const response = await fetch(
          `https://xianhefairy.com/LotusFairy_API/api/Booking/getTotalCommission?ID=${userID}`,
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        return response.json();
      });

      setComCoins(data?.[0]?.COINS ?? 0);
    } catch (error) {
      console.error("Error fetching coins:", error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchComs();
  }, []);

  useEffect(() => {
    const userName = localStorage.getItem("username");
    const userID = localStorage.getItem("userID");
    const langId = localStorage.getItem('languageId') || '2224'; // Get from storage with fallback
    console.log("Fetching profile for userID:", userID, "with LangId:", langId);

    const fetchData = async () => {
      try {
        const result = await getCachedRequest(
          `profile:${userID}:${langId}`,
          async () => {
            const response = await fetch(
              "https://xianhefairy.com/LotusFairy_API/api/User/GetProfile",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  UserName: userName,
                  ID: userID,
                  LangId: parseInt(langId),
                  UserToken: "",
                  Token: {
                    AccessToken:
                      "xAfhn90xflfHfd.fjldfj@fdhLXHCHchlhsdflsdfhawWFRwzxxp",
                  },
                }),
              },
            );

            const profileResult = await response.json();

            if (!response.ok) {
              throw new Error(
                `Error: ${response.status} - ${profileResult.message || "Request failed"}`,
              );
            }

            return profileResult;
          },
        );

        console.log("API Response:", result); // Log the entire response

        if (result?.Profiles?.[0]) {
          const profile = result.Profiles[0];
          setEmail(profile.Email || "");
          setUsername(profile.FullName || "");
          setDob(profile.DOB || "");
          setChineseDob(profile.CDOB || "");
          setCountry(profile.Country || "");
          setGender(profile.Gender || "");
          setMobileNumber(profile.MobileNo || "");
          setVP(profile.VP_CODE || "");
          setVVP(profile.VVP_CODE || "");
          setMember(profile.UPLINE_VP_CODE || "Nil");
          const interestData = profile.Interest;
          if (Array.isArray(interestData) && interestData.length > 0) {
            const descriptions = interestData.map(item => item.Description).join(", ");
            setInterest(descriptions);
          } else {
            setInterest("N/A");
          }
          setAge(profile.AGE ?? null);
          setAletaMemberNo(profile.AletaMemberNo || "");
          setAletaAccountNo(profile.AletaAccountNo || "");
          setAletaAccountSerialNo(profile.AletaAccountSerialNo || "");

          if (profile.DOB) {
            LunarConvert(profile.DOB);
          }

          console.log(result);
        } else {
          throw new Error("Profile data is missing from the API response.");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // Fetch available languages and listen for language changes
  useEffect(() => {
    const fallbackLanguages = [
      { Id: '2224', LookupDetail: 'en', LookupDesc: 'English' },
      { Id: '2225', LookupDetail: 'id', LookupDesc: 'Bahasa Indonesia' },
      { Id: '2226', LookupDetail: 'cn', LookupDesc: 'Chinese' },
    ];

    fetchLanguages().then(languages => {
      if (!languages || languages.length === 0) {
        setAvailableLanguages(fallbackLanguages);
        return;
      }

      const mergedLangs = [...languages];
      const existingCodes = mergedLangs.map(lang => {
        let code = (lang.LookupDetail || '').match(/^(\S+)/)?.[1]?.toLowerCase() || '';
        if (code.startsWith('zh')) code = 'cn';
        return code;
      });

      fallbackLanguages.forEach(fallback => {
        let fallbackCode = (fallback.LookupDetail || '').match(/^(\S+)/)?.[1]?.toLowerCase() || '';
        if (fallbackCode.startsWith('zh')) fallbackCode = 'cn';
        if (!existingCodes.includes(fallbackCode)) {
          mergedLangs.push(fallback);
        }
      });

      setAvailableLanguages(mergedLangs);
    }).catch(() => {
      setAvailableLanguages(fallbackLanguages);
    });
  }, []);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isButtonVisible, setButtonVisible] = useState(true);

  // Get short language label based on the languageId used in the profile call
  const getLangLabel = () => {
    const langId = localStorage.getItem('languageId') || '2224';
    const match = availableLanguages.find(l => String(l.Id) === String(langId));
    if (!match) return selectedLanguage.toUpperCase();
    return match.LookupDetail.split(' ')[0].substring(0, 3).toUpperCase();
  };

  // Delete account modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeletedModal, setShowDeletedModal] = useState(false);
  const [deletedMessage, setDeletedMessage] = useState("");

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleOpenDeleteModal = () => {
    setDeletePassword("");
    setDeleteError("");
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletePassword("");
    setDeleteError("");
    setIsDeleting(false);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleConfirm = (data) => {
    console.log("Form data:", data);
    setButtonVisible(false);
  };

  const handleDeleteAccount = async () => {
    setDeleteError("");
    if (!deletePassword) {
      setDeleteError("Please enter your password.");
      return;
    }
    setIsDeleting(true);
    try {
      const url = `https://xianhefairy.com/LotusFairy_API/api/User/DeleteAccount?CustId=${userID}&Password=${encodeURIComponent(
        deletePassword,
      )}`;
      const res = await fetch(url, { method: "POST" });
      const result = await res.json();

      if (result && result.successful === true) {
        // show a confirmation modal first, then logout on user action
        setDeletedMessage(result.Message || "Your account has been deleted.");
        handleCloseDeleteModal();
        setShowDeletedModal(true);
      } else {
        setDeleteError(result.Message || "Failed to delete account.");
      }
    } catch (err) {
      console.error(err);
      setDeleteError("An error occurred. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const performLogout = () => {
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("userID");
    localStorage.removeItem("username");
    localStorage.removeItem("token");
    setShowDeletedModal(false);
    navigate("/main", { replace: true });
  };

  useEffect(() => {
    let t;
    if (showDeletedModal) {
      // auto-logout after 4s as a fallback
      t = setTimeout(() => {
        performLogout();
      }, 4000);
    }
    return () => clearTimeout(t);
  }, [showDeletedModal]);

  // Format any parseable date value as "D-MMM-YYYY", e.g. 5-Aug-1999, using localized month names
  const formatDateDDMMMYYYY = (value) => {
    if (!value) return "";
    const monthNames = t("months.short", { returnObjects: true, defaultValue: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] });
    let year, month, day;

    const datePart = String(value).split(" ")[0];
    const isoMatch = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      // Parse components directly to avoid UTC/local timezone shifting the day
      year = parseInt(isoMatch[1], 10);
      month = parseInt(isoMatch[2], 10) - 1;
      day = parseInt(isoMatch[3], 10);
    } else {
      const parsed = new Date(value);
      if (isNaN(parsed.getTime())) return value;
      year = parsed.getFullYear();
      month = parsed.getMonth();
      day = parsed.getDate();
    }

    return `${day}-${monthNames[month]}-${year}`;
  };

  const isDob2077OrLater = () => {
    const year = age;
    return year >= 2077;
  };

  const LunarConvert = async (dobSet) => {
    try {
      const dobDate = new Date(dobSet);
      const year = dobDate.getFullYear();
      const month = dobDate.getMonth() + 1;
      const day = dobDate.getDate();
      const hour = dobDate.getHours();
      const minute = dobDate.getMinutes();

      const data = await getCachedRequest(`lunar:${year}:${month}:${day}`, async () => {
        const url = `https://xianhefairy.com/LotusFairy_API/api/member/GetChineseDOB?cYear=${year}&cMonth=${month}&cDay=${day}`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error("Failed to fetch Chinese DOB");
        }

        return response.json();
      });

      // Extract only the date part from the API response (remove any time if present)
      const chineseDate = data?.[0]?.A1?.split(" ")[0]; // Get only the date part

      if (!chineseDate) {
        return;
      }

      // Format the time from the original DOB
      const formattedTime = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;

      // Combine Chinese date with original time
      const chineseDateWithTime = `${formatDateDDMMMYYYY(chineseDate)} ${formattedTime}`;

      setChineseDob(chineseDateWithTime);
      console.log("Chinese DOB with time:", chineseDateWithTime);
    } catch (error) {
      console.error("Error fetching Chinese DOB:", error);
    }
  };

  //reset pass
  const [step, setStep] = useState(0); // 0 = hidden, 1 = enter current pass, 2 = new password form
  const [currentPass, setCurrentPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [reNewPass, setReNewPass] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const resetpass = () => {
    setStep(2); // show current password modal
  };

  // const openAletaWallet = () => {
  //   navigate("/wallet");
  // };

  const handleCheckCurrentPassword = async () => {
    const body = {
      UserName: email,
      Password: currentPass,
      Token: {
        AccessToken: "xAfhn90xflfHfd.fjldfj@fdhLXHCHchlhsdflsdfhawWFRwzxxp",
      },
    };
    try {
      const res = await fetch(
        "https://xianhefairy.com/LotusFairy_API/api/Values/SubmitLogin",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        },
      );

      const result = await res.json();
      if (result && result.ID !== null && result.ID !== undefined) {
        setStep(2); // move to new password modal
        setErrorMsg("");
      } else {
        setErrorMsg("Incorrect password. Try again.");
        console.log(body);
      }
    } catch (err) {
      setErrorMsg("An error occurred. Try again.");
      console.error(err);
    }
  };

  const handleChangePassword = async () => {
    // Validate password complexity: min 6 chars, 1 upper, 1 lower, 1 number, 1 special
    const pwdRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\[\]{};':"\\|,.<>\/?`~\-]).{6,}$/;
    if (!pwdRegex.test(newPass)) {
      setErrorMsg(t('signUpPage.passwordRequirements') || 'Password must be at least 6 characters and include uppercase, lowercase, number and special character.');
      return;
    }
    const body = {
      Username: email,
      NewPassword: newPass,
    };
    if (newPass !== reNewPass) {
      setErrorMsg(t('loginPage.passwordNotMatch') || "Passwords do not match.");
      return;
    }

    setIsUpdatingPassword(true);
    try {
      const res = await fetch(
        "https://xianhefairy.com/LotusFairy_API/api/booking/ResetPassword",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        },
      );

      const result = await res.json();

      if (res.ok && result?.Success === true) {
        setStep(0); // close modal
        setCurrentPass("");
        setNewPass("");
        setReNewPass("");
        setErrorMsg("");
        alert("Password changed successfully.");
      } else {
        setErrorMsg(result?.Message || "Failed to update password.");
      }
    } catch (error) {
      setErrorMsg("Error updating password.");
      console.error(error);
    }
    finally {
      setIsUpdatingPassword(false);
    }
    console.log(body);
  };

  const [copiedCode, setCopiedCode] = useState(""); // Add this state
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);

  // Password visibility toggles
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [showDeletePass, setShowDeletePass] = useState(false);
  // Password update loading state
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const getShareMessage = (code) =>
    `Signup to Hé by SiewLi! Use my referral code ${code}.\nSignup for free: https://xianhefairy.com/signup/${code}`;

  const handleShare = (code) => {
    const message = getShareMessage(code);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(message);
    } else {
      // fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = message;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(""), 2000); // Hide after 2 seconds

    setShowCopyToast(true);
    setTimeout(() => setShowCopyToast(false), 2200);
  };

  // Determine QR code URL and visibility
  const getQRCodeData = () => {
    if (vvp) {
      // IC available - prioritize IC
      return {
        visible: true,
        url: `https://xianhefairy.com/signup/${vvp}`,
        type: 'IC',
        code: vvp
      };
    } else if (vp) {
      // AP available (IC not available)
      return {
        visible: true,
        url: `https://xianhefairy.com/signup/${vp}`,
        type: 'AP',
        code: vp
      };
    } else {
      // Free explorer - default signup link
      return {
        visible: true,
        url: `https://xianhefairy.com/signup/XH555`,
        type: 'FREE',
        code: 'XH555'
      };
    }
  };

  const qrCodeData = getQRCodeData();

  const handleOpenQRModal = () => {
    if (qrCodeData.visible) {
      setIsQRModalOpen(true);
    }
  };

  const handleCloseQRModal = () => {
    setIsQRModalOpen(false);
  };

  return (
    <div
      style={{
        ...responsiveStyles.panel,
        overflowY: "visible",
        maxHeight: "none",
      }}
      className="terms-modal-content"
    >
      {/* Main Profile Card */}
      <div
        style={{
          backgroundImage: `linear-gradient(rgba(15,10,30,0.82), rgba(15,10,30,0.88)), url(${profilebg})`,
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "100% 100%",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "16px",
          padding: isSmallMobile ? "16px" : isMobile ? "20px" : "28px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: isMobile ? "center" : "flex-end",
            gap: "10px",
            marginBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => navigate("/purchasehistory")}
            style={{
              padding: "8px 16px",
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.4)",
              borderRadius: "8px",
              color: "#fff",
              cursor: "pointer",
              fontSize: isMobile ? "12px" : "14px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <FaHistory size={13} /> {t("account.accounthistory", { defaultValue: "History" })}
          </button>
          {/* <button
            onClick={() =>
              navigate("/account", {
                state: { setactiveTab: "unsubscribe" },
              })
            }
            style={{
              padding: "8px 16px",
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.4)",
              borderRadius: "8px",
              color: "#fff",
              cursor: "pointer",
              fontSize: isMobile ? "12px" : "14px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <MdSubscriptions size={14} />{" "}
            {t("account.subscriptionManagement", { defaultValue: "Subscription Management" })}
          </button> */}
        </div>

        {/* Profile header: avatar + info */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "center" : "flex-start",
            gap: "24px",
            marginBottom: "24px",
          }}
        >
          {/* Avatar with purple glow ring */}
          <div
            style={{
              flexShrink: 0,
              width: isMobile ? "96px" : "116px",
              height: isMobile ? "96px" : "116px",
              borderRadius: "50%",
              padding: "3px",
              backgroundImage: `url(${profileContainer})`,
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              // boxShadow: "0 0 20px rgba(120,60,180,0.7), 0 0 40px rgba(100,40,160,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                overflow: "hidden",
                background: "rgba(26, 16, 51, 0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: isMobile ? "8px" : "10px",
                boxSizing: "border-box",
              }}
            >
              <ProfilePictureUpload size={isMobile ? 72 : 86} />
            </div>
          </div>

          {/* User details */}
          <div style={{ flex: 1, textAlign: isMobile ? "center" : "left" }}>
            {/* Name */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: isMobile ? "center" : "flex-start",
                marginBottom: "12px",
              }}
            >
              <span
                style={{
                  color: "#DFBD69",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "bold",
                }}
              >
                {username}
              </span>
              <span style={{ fontSize: "18px" }}></span>
            </div>

            {/* DOB and Age */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "20px",
                marginBottom: "8px",
                justifyContent: isMobile ? "center" : "flex-start",
              }}
            >
              <span style={{ color: "#fff", fontSize: isMobile ? "13px" : "15px" }}>
                <span style={{ color: "#aaaaaa" }}>{t("account.dateOfBirth")}:</span>{" "}
                {formatDateDDMMMYYYY(dob)}
              </span>
              {age !== null && (
                <span style={{ color: "#fff", fontSize: isMobile ? "13px" : "15px" }}>
                  <span style={{ color: "#aaaaaa" }}>
                    {t("account.age", { defaultValue: "Age" })}:
                  </span>{" "}
                  {age}
                </span>
              )}
            </div>

            {/* Language */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "16px",
                justifyContent: isMobile ? "center" : "flex-start",
              }} 
            >
              <span style={{ color: "#aaaaaa", fontSize: isMobile ? "13px" : "15px" }}>
                {t("account.language", { defaultValue: "Language" })}:
              </span>
              <span style={{ color: "#fff", fontSize: isMobile ? "13px" : "15px" }}>{getLangLabel()}</span>
              {/* <span style={{ color: "#DFBD69", fontSize: "16px" }}>⇄</span> */}
            </div>

            {/* Edit Profile + Settings buttons */}
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: isMobile ? "center" : "flex-start",
                flexWrap: "wrap",
              }}
            >
              {/* <button
                onClick={handleOpenModal}
                style={{
                  padding: "8px 16px",
                  background: "#DFBD69",
                  border: "1px solid #DFBD69",
                  borderRadius: "8px",
                  color: "black",
                  cursor: "pointer",
                  fontSize: isMobile ? "13px" : "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <FaUserEdit size={14} />{" "}
                {t("account.editProfile", { defaultValue: "Edit Profile" })}
              </button> */}
              <button
                onClick={() => navigate("/settings")}
                style={{
                  padding: "8px 16px",
                  background: "transparent",
                  border: "1px solid #DFBD69",
                  borderRadius: "8px",
                  color: "#DFBD69",
                  cursor: "pointer",
                  fontSize: isMobile ? "13px" : "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <FaCog size={14} />{" "}
                {t("account.settings", { defaultValue: "Settings" })}
              </button>
            </div>
          </div>
        </div>

        {/* Divider */}
        <hr
          style={{
            border: "none",
            borderTop: "1px solid rgba(255,255,255,0.1)",
            margin: "0 0 20px",
          }}
        />

        {/* Gems / Top up / VP row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: "20px",
                color: "#fff",
                fontSize: isMobile ? "13px" : "14px",
              }}
            >
              <FaGem style={{ color: "#9B7AE8" }} size={13} />{" "}
              {coins} {t("account.gems", { defaultValue: "GEMS" })}
            </div>
            <button
              onClick={() =>
                navigate("/account", {
                  state: { setactiveTab: "purchasecoin" },
                })
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                background: "rgba(223,189,105,0.1)",
                border: "1px solid rgba(223,189,105,0.35)",
                borderRadius: "20px",
                color: "#DFBD69",
                cursor: "pointer",
                fontSize: isMobile ? "13px" : "14px",
              }}
            >
              🪙 {t("tryoutpage.topup")}
            </button>
            {(vp || vvp) && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 16px",
                  background: "rgba(223,189,105,0.1)",
                  border: "1px solid rgba(223,189,105,0.35)",
                  borderRadius: "20px",
                  color: "#DFBD69",
                  fontSize: isMobile ? "13px" : "14px",
                }}
              >
                <FaCrown size={13} /> VP
              </div>
            )}
          </div>

          {qrCodeData.visible && (
            <button
              onClick={handleOpenQRModal}
              title={`Show ${qrCodeData.type} QR Code`}
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: "8px",
                color: "#fff",
                cursor: "pointer",
                padding: "6px 10px",
                display: "flex",
                alignItems: "center",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = "rgba(223,189,105,0.5)";
                e.target.style.color = "#DFBD69";
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = "rgba(255,255,255,0.25)";
                e.target.style.color = "#fff";
              }}
            >
              <FaQrcode size={18} />
            </button>
          )}
        </div>

        {/* Divider */}
        <hr
          style={{
            border: "none",
            borderTop: "1px solid rgba(255,255,255,0.1)",
            margin: "0 0 20px",
          }}
        />

        {/* Interest section */}
        <div style={{ marginBottom: "20px" }}>
          <p
            style={{
              color: "#aaaaaa",
              fontSize: isMobile ? "14px" : "15px",
              marginBottom: "12px",
            }}
          >
            {t("account.interest")}:
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            {interest && interest !== "null" && interest !== "N/A"
              ? interest.split(", ").map((tag, i) => (
                  <span
                    key={i}
                    style={{
                      padding: "6px 16px",
                      border: "1px solid rgba(255,255,255,0.25)",
                      borderRadius: "20px",
                      color: "#fff",
                      fontSize: isMobile ? "12px" : "13px",
                    }}
                  >
                    {tag}
                  </span>
                ))
              : null}
          </div>
        </div>

        {/* Divider */}
        <hr
          style={{
            border: "none",
            borderTop: "1px solid rgba(255,255,255,0.1)",
            margin: "0 0 20px",
          }}
        />

        {/* Additional account details */}
        <div>
          <p style={{ color: "#aaaaaa", fontSize: isMobile ? "13px" : "14px", marginBottom: "10px" }}>
            {t("account.email")}:{" "}
            <span style={{ color: "#fff" }}>{email}</span>
          </p>
          <p style={{ color: "#aaaaaa", fontSize: isMobile ? "13px" : "14px", marginBottom: "10px" }}>
            {t("account.chineseDateOfBirth")}:{" "}
            <span style={{ color: "#fff" }}>{chineseDob}</span>
          </p>
          <p style={{ color: "#aaaaaa", fontSize: isMobile ? "13px" : "14px", marginBottom: "10px" }}>
            {t("account.gender")}:{" "}
            <span style={{ color: "#fff" }}>
              {gender?.toLowerCase() === "male"
                ? t("account.male")
                : gender?.toLowerCase() === "female"
                  ? t("account.female")
                  : gender}
            </span>
          </p>
          <p style={{ color: "#aaaaaa", fontSize: isMobile ? "13px" : "14px", marginBottom: "10px" }}>
            {t("account.mobileNumber")}:{" "}
            <span style={{ color: "#fff" }}>{mobileNumber}</span>
          </p>
          {(vvp || vp) && (
            <p style={{ color: "#aaaaaa", fontSize: isMobile ? "13px" : "14px", marginBottom: "10px" }}>
              {t("account.commission")}:{" "}
              <span style={{ color: "#fff" }}>US${comcoins}</span>
            </p>
          )}
          {vvp && (
            <p style={{ color: "#aaaaaa", fontSize: isMobile ? "13px" : "14px", marginBottom: "10px" }}>
              {t("account.ic")}:{" "}
              <button
                onClick={() => handleShare(vvp)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                title={t("account.copyShareMessage")}
              >
                <img
                  src={shareIcon}
                  alt={t("account.share")}
                  style={{ width: "14px", height: "14px", verticalAlign: "middle" }}
                />
                <span style={{ textDecoration: "underline", color: "#DFBD69" }}>{vvp}</span>
                {copiedCode === vvp && (
                  <span style={{ marginLeft: "6px", color: "#DFBD69", fontSize: "12px" }}>
                    {t("account.linkCopied")}
                  </span>
                )}
              </button>
            </p>
          )}
          {!vvp && vp && (
            <p style={{ color: "#aaaaaa", fontSize: isMobile ? "13px" : "14px", marginBottom: "10px" }}>
              {t("account.ap")}:{" "}
              <button
                onClick={() => handleShare(vp)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
                title={t("account.copyShareMessage")}
              >
                <img
                  src={shareIcon}
                  alt={t("account.share")}
                  style={{ width: "14px", height: "14px", verticalAlign: "middle" }}
                />
                <span style={{ textDecoration: "underline", color: "#DFBD69" }}>{vp}</span>
                {copiedCode === vp && (
                  <span style={{ marginLeft: "6px", color: "#DFBD69", fontSize: "12px" }}>
                    {t("account.linkCopied")}
                  </span>
                )}
              </button>
            </p>
          )}
          {/* {aletaMemberNo ? (
            <p style={{ color: "#aaaaaa", fontSize: isMobile ? "13px" : "14px", marginBottom: "10px" }}>
              {t("account.aletaMemberNo")}:{" "}
              <span style={{ color: "#fff" }}>{aletaMemberNo}</span>
            </p>
          ) : null}
          {aletaAccountNo ? (
            <p style={{ color: "#aaaaaa", fontSize: isMobile ? "13px" : "14px", marginBottom: "10px", wordBreak: "break-all" }}>
              {t("account.aletaAccountNo")}:{" "}
              <span style={{ color: "#fff" }}>{aletaAccountNo}</span>
            </p>
          ) : null}
          {aletaAccountSerialNo ? (
            <p style={{ color: "#aaaaaa", fontSize: isMobile ? "13px" : "14px", marginBottom: "10px" }}>
              {t("account.aletaAccountSerialNo")}:{" "}
              <span style={{ color: "#fff" }}>{aletaAccountSerialNo}</span>
            </p>
          ) : null} */}
          {/* {(vvp || vp) && (
            <p style={{ color: "#aaaaaa", fontSize: isMobile ? "13px" : "14px", marginBottom: "10px" }}>
              Aleta Wallet:{" "}
              <button style={{ ...styles.changePassButton, color: "#DFBD69" }} onClick={openAletaWallet}>
                Retrieve details
              </button>
            </p>
          )} */}
          {/* <p style={{ color: "#aaaaaa", fontSize: isMobile ? "13px" : "14px", marginBottom: "10px" }}>
            {t("account.changePassword")}?{" "}
            <button style={{ ...styles.changePassButton, color: "#DFBD69" }} onClick={resetpass}>
              {t("account.clickHere")}
            </button>
          </p>
          <p style={{ color: "#aaaaaa", fontSize: isMobile ? "13px" : "14px", marginBottom: "10px" }}>
            {t("account.deleteAccount")}?{" "}
            <button
              onClick={handleOpenDeleteModal}
              style={{ ...styles.changePassButton, color: "#c0392b" }}
            >
              {t("account.deleteAccount")}
            </button>
          </p> */}
        </div>
      </div>

      {isDob2077OrLater() && (
        <div style={{ marginTop: "40px", textAlign: "center" }}>
          <button
            style={{
              ...styles.button,
              padding: "15px 40px",
              fontSize: "18px",
            }}
            onClick={handleOpenModal}
          >
            <FaUserEdit /> {t("account.oneTimeEditProfile")}
          </button>
        </div>
      )}

      {isModalOpen && (
        <EditPage
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirm}
        />
      )}

      {step === 2 && (
        <div
          // clicking outside previously closed the modal; disabled to require explicit close
          onClick={() => {/* setStep(0) */}}
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background:
                "linear-gradient(to bottom, #DFBD69, #b7954d, #926F34)",
              padding: "20px",
              paddingHorizontal: "40px",
              borderRadius: "8px",
              width: "400px",
              textAlign: "center",
              position: "relative",
            }}
          >
            <button
              type="button"
              onClick={() => setStep(0)}
              aria-label={t("account.close") || "Close"}
              style={{
                position: "absolute",
                top: "2px",
                right: "8px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontSize: "30px",
                color: "#000",
              }}
            >
              ×
            </button>
            <div style={{ marginBottom: "25px", color: "#000" }}/>
            <h3>{t("account.setNewPassword")}</h3>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
              <input
                type={showNewPass ? "text" : "password"}
                value={newPass}
                onChange={(e) => setNewPass(e.target.value)}
                placeholder={t("account.newPassword")}
                style={{ flex: 1, padding: "8px" }}
              />
              <button
                type="button"
                onClick={() => setShowNewPass((s) => !s)}
                aria-label={showNewPass ? "Hide password" : "Show password"}
                style={{ background: "transparent", border: "none", cursor: "pointer", color: "#000" }}
              >
                {showNewPass ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
              <input
                type={showConfirmPass ? "text" : "password"}
                value={reNewPass}
                onChange={(e) => setReNewPass(e.target.value)}
                placeholder={t("account.reEnterPassword")}
                style={{ flex: 1, padding: "8px" }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPass((s) => !s)}
                aria-label={showConfirmPass ? "Hide password" : "Show password"}
                style={{ background: "transparent", border: "none", cursor: "pointer", color: "#000" }}
              >
                {showConfirmPass ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errorMsg && (
              <p style={{ color: "white", fontSize: "14px" }}>{errorMsg}</p>
            )}
            <button
              onClick={handleChangePassword}
              style={{ ...styles.button, width: "100%" }}
              disabled={isUpdatingPassword}
            >
              {isUpdatingPassword ? ("⏳ " + (t("account.updating") || "Updating...")) : t("account.updatePassword")}
            </button>
          </div>
        </div>
      )}

      {isDeleteModalOpen && (
        <div
          onClick={handleCloseDeleteModal}
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background:
                "linear-gradient(to bottom, #DFBD69, #b7954d, #926F34)",
              padding: "20px",
              borderRadius: "8px",
              width: "380px",
              textAlign: "center",
            }}
          >
            <h3>{t("account.confirmAccountDeletion")}</h3>
            <p style={{ color: "#000" }}>{t("account.deleteAccountWarning")}</p>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type={showDeletePass ? "text" : "password"}
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder={t("account.password")}
                style={{ flex: 1, padding: "8px", marginBottom: "10px" }}
              />
              <button
                type="button"
                onClick={() => setShowDeletePass((s) => !s)}
                aria-label={showDeletePass ? "Hide password" : "Show password"}
                style={{ background: "transparent", border: "none", cursor: "pointer", color: "#000" }}
              >
                {showDeletePass ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {deleteError && (
              <p style={{ color: "red", fontSize: "14px" }}>{deleteError}</p>
            )}
            <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
              <button
                onClick={handleCloseDeleteModal}
                style={{ ...styles.button, flex: 1, backgroundColor: "#666" }}
                disabled={isDeleting}
              >
                {t("account.cancel")}
              </button>
              <button
                onClick={handleDeleteAccount}
                style={{
                  ...styles.button,
                  flex: 1,
                  backgroundColor: "#c0392b",
                  color: "#fff",
                }}
                disabled={isDeleting}
              >
                {isDeleting ? t("account.deleting") : t("account.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {isQRModalOpen && qrCodeData.visible && (
        <div
          onClick={handleCloseQRModal}
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2001,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#0f0a1e",
              border: "1px solid rgba(255,255,255,0.1)",
              padding: "30px",
              borderRadius: "16px",
              textAlign: "center",
              maxWidth: "500px",
              width: isMobile ? "90%" : "auto",
              boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
            }}
          >
            <button
              type="button"
              onClick={handleCloseQRModal}
              aria-label={t("account.close") || "Close"}
              style={{
                position: "absolute",
                top: "12px",
                right: "16px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontSize: "28px",
                color: "#DFBD69",
              }}
            >
              ×
            </button>
            
            <h3 style={{ marginBottom: "20px", color: "#DFBD69" }}>
              {qrCodeData.type} {t("account.qrCode", { defaultValue: "QR Code" })}
            </h3>
            
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "20px",
                padding: "15px",
                backgroundColor: "#fff",
                borderRadius: "8px",
              }}
            >
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(
                  qrCodeData.url
                )}`}
                alt={`${qrCodeData.type} QR Code`}
                style={{
                  maxWidth: "300px",
                  width: "100%",
                  height: "auto",
                }}
              />
            </div>
            
            <p style={{ color: "#aaaaaa", marginBottom: "20px", fontSize: "14px" }}>
              {t("account.qrCodeDescription", {
                defaultValue: `Scan this QR code to share your ${qrCodeData.type} link`
              })}
            </p>
            
            <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap" }}>
              <button
                onClick={() => {
                  handleShare(qrCodeData.code);
                  handleCloseQRModal();
                }}
                style={{
                  ...styles.button,
                  backgroundColor: "#DFBD69",
                  color: "#000",
                  padding: "10px 20px",
                  fontWeight: "bold",
                }}
              >
                {t("account.share", { defaultValue: "Share" })}
              </button>
              <button
                onClick={handleCloseQRModal}
                style={{
                  ...styles.button,
                  backgroundColor: "transparent",
                  color: "#DFBD69",
                  border: "1px solid #DFBD69",
                  padding: "10px 20px",
                }}
              >
                {t("account.close", { defaultValue: "Close" })}
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        role="status"
        aria-live="polite"
        style={{
          position: "fixed",
          bottom: "24px",
          left: "50%",
          transform: showCopyToast
            ? "translate(-50%, 0)"
            : "translate(-50%, 16px)",
          opacity: showCopyToast ? 1 : 0,
          pointerEvents: "none",
          transition: "opacity 0.25s ease, transform 0.25s ease",
          backgroundColor: "#1a1428",
          border: "1px solid #DFBD69",
          color: "#DFBD69",
          padding: "10px 20px",
          borderRadius: "999px",
          fontSize: "14px",
          fontWeight: 600,
          boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
          zIndex: 9999,
        }}
      >
        {t("account.linkCopied")}
      </div>
    </div>
  );
};

export default AccountInfoPanel;
